<template>
  <v-form
    class="login-form"
    @submit.prevent="onSubmit"
  >
    <h1 class="nds-text-h1 title">
      {{ title }}
    </h1>

    <div>
      <div class="input-block">
        <div class="label nds-text-p-bold">
          {{ $t('email_address') }}
        </div>

        <div
          class="input"
          behaviour="username"
        >
          <input-element
            v-model="form_data.email"
            :error="email_error"
            :placeholder="$t('email_address')"
            @change="onEmailChanged()"
          />
        </div>
      </div>

      <div
        v-if="!is_forgot_password"
        class="input-block"
      >
        <div class="label nds-text-p-bold">
          {{ $t('password') }}
        </div>

        <div
          class="input"
          behaviour="password"
        >
          <input-password-element
            v-model="form_data.password"
            :placeholder="$t('password')"
          />
        </div>
      </div>
    </div>

    <div class="forgot-password">
      <p
        class="nds-text-p-big-bold"
        @click="onToggle"
      >
        <v-icon size="16">
          mdi-chevron-right
        </v-icon>

        {{ forgot_password_text }}
      </p>
    </div>

    <button-element
      class="submit-button"
      color="blue"
      :disabled="button_disabled"
      :text="button_text"
      type="submit"
    />

    <div
      v-if="reset_password_mail_sent"
      class="email-sent"
    >
      <svg-element
        icon="check"
        :size="20"
      />

      <p class="nds-text-p-big-bold">
        {{ $t('forgot_password.reset_password_mail_sent') }}
      </p>
    </div>
  </v-form>
</template>

<script>
import { mapActions } from 'vuex';

import configuration from '@/configurations';
import router from '@/router';

import ButtonElement from '../ButtonElement.vue';
import InputElement from '@/components/elements/InputElement.vue';
import InputPasswordElement from '@/components/elements/InputPasswordElement.vue';
import SvgElement from '../SvgElement.vue';

export default {
  name: 'LoginFormElement',
  components: {
    ButtonElement,
    InputElement,
    InputPasswordElement,
    SvgElement,
  },
  props: {
    is_forgot_password: {
      type: Boolean,
      required: true,
    },
    toggle_forgot_password: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      email_error: false,
      form_data: {
        email: '',
        password: '',
      },
      is_valid: true,
      loading_login: false,
      reset_password_mail_sent: false,
    };
  },
  computed: {
    block() {
      return this.is_forgot_password
        ? 'forgot_password.'
        : 'login.';
    },
    title() {
      return this.$t(this.block + 'title');
    },
    forgot_password_text() {
      return this.$t(this.block + 'toggle_text');
    },
    button_disabled() {
      return (
        !this.is_forgot_password
        && (
          this.loading_login
          || this.email_error
          || this.form_data.email.length === 0
          || this.form_data.password.length === 0
        ) || (
          this.is_forgot_password
          && (
            this.loading_login
            || this.email_error
            || this.form_data.email.length === 0
          )
        )
      );
    },
    button_text() {
      return this.$t(this.block + 'button_text');
    },
  },
  mounted() {
    // Fix for Google Autofill - 2024-11-19 (DA)
    this.changeButtonColor();
  },
  methods: {
    ...mapActions({
      login: 'user/login',
      resetPassword: 'user/resetPassword',
      triggerToast: 'toast/triggerToast',
    }),
    changeButtonColor() {
      const button = document.querySelector('.submit-button');

      button.classList.remove('button--disabled');
    },
    onEmailChanged() {
      this.email_error = !/.+@.+\..+/.test(this.form_data.email);
    },
    async onSubmit() {
      if (this.loading_login) {
        return;
      }

      this.loading_login = true;

      try {
        if (this.is_valid) {
          if (this.is_forgot_password) {
            await this.resetPassword(this.form_data.email);
            this.reset_password_mail_sent = true;
            this.form_data.email = '';
          } else {
            await this.login(this.form_data);

            if (this.$route.query.redirect_to !== undefined) {
              if (
                this.$route.query.front_version !== undefined
                && this.$route.query.front_version === 'v1'
              ) {
                window.location.href = `${configuration.desk_v1_host}${this.$route.query.redirect_to}`;

                return;
              } else {
                router.push({
                  path: this.$route.query.redirect_to,
                });
              }
            } else {
              router.push('/');
            }
          }
        }
      } catch(e) {
        this.loading_login = false;

        this.triggerToast({
          style: 'error',
          text: this.$t('credentials_invalid'),
        });
      }
    },
    onToggle() {
      this.toggle_forgot_password();
      this.form_data.email = '';
      this.form_data.password = '';
      this.reset_password_mail_sent = false;
      // this.$refs.form.resetValidation();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/constants/variables.module.scss';

.login-form {
  background-color: $white;
  border: 2px solid $whisper;
  border-radius: 20px;
  box-shadow: 4px 4px 10px 0px rgba(0, 0, 0, 0.05);
  margin: auto;
  padding: 40px;
  width: 540px;

  .forgot-password {
    align-items: center;
    color: $cornflower-blue;
    cursor: pointer;
    display: flex;
    justify-content: flex-end;
    margin-bottom: 40px;

    p {
      align-items: center;
      color: $cornflower-blue;
      cursor: pointer;
      display: flex;
      margin-bottom: 0 !important;
    }
  }

  .email-sent {
    align-items: flex-start;
    display: flex;
    gap: 8px;
    margin-top: 16px;

    svg {
      --color: #{$jade};
      overflow: visible;
      transform: translateY(2px);
    }

    p {
      text-align: justify;
    }
  }

  .title {
    margin-bottom: 40px;
    margin-top: 0;
    text-align: center;
  }

  .label {
    text-transform: uppercase;
  }

  .submit-button {
    border-radius: 20px;
    font-size: 16px;
    padding: 10px 0;
    width: 100%;
  }

  .password {
    margin-top: 16px;
  }
}

.input-block {
  margin-bottom: 24px;
}

.input {
  margin-top: 8px;
}
</style>

<i18n src="@/locales/common.json"></i18n>

<i18n src="@/locales/components/elements/logins/login-form-element.json"></i18n>
